import type { InitOptions } from 'i18next';
import { z } from 'zod';

export const DEFAULT_LANGUAGE = 'en';

export const i18nConfig: InitOptions = {
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  // Use old plural handling
  // Reference: https://www.i18next.com/misc/migration-guide#v20.x.x-to-v21.0.0
  compatibilityJSON: 'v3',
  // i18next uses natural natural language selection after v21 which breaks our current ability to access keys with spaces.
  // To guard against this breaking change, we now manually set `nsSeparator` and `keySeparator`.
  // Reference https://www.i18next.com/misc/migration-guide#natural-language-detection
  nsSeparator: ':',
  keySeparator: false,
  debug: false,
};

export const languageCodes = [
  'en',
  'de',
  'fr',
  'es',
  'pt',
  'it',
  'fil',
  'ru',
  'tr',
  'id',
  'pl',
  'ja',
  'ar',
  'nl',
  'fi',
  'da',
  'nb',
  'sv',
] as const;

export const languageCodesSchema = z.enum(languageCodes).catch(DEFAULT_LANGUAGE);

export type LanguageCode = (typeof languageCodes)[number];

/**
 * Checks if the given value is a valid language code.
 * @param value {string}
 * @returns {boolean}
 */
export const isLanguageCode = (value: string): value is LanguageCode => languageCodes.includes(value as LanguageCode);

export const languageTitles = [
  'English',
  'Deutsch',
  'Français',
  'Español',
  'Português (Brasil)',
  'Italiano',
  'Filipino',
  'Русский',
  'Türkçe',
  'Bahasa Indonesia',
  'Polski',
  '日本語',
  'العربية', // TODO: review with Nada
  'Nederlands', // TODO: verify with Anna
  'Finnish',
  'Danish',
  'Norwegian Bokmål',
  'Svenska',
] as const;

export type LanguageTitle = (typeof languageTitles)[number];

export const languageCodeToTitleMap: Record<LanguageCode, LanguageTitle> = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  pt: 'Português (Brasil)',
  it: 'Italiano',
  fil: 'Filipino',
  ru: 'Русский',
  tr: 'Türkçe',
  id: 'Bahasa Indonesia',
  pl: 'Polski',
  ja: '日本語',
  ar: 'العربية',
  nl: 'Nederlands',
  fi: 'Finnish',
  da: 'Danish',
  nb: 'Norwegian Bokmål',
  sv: 'Svenska',
};

export const languageLocaleCodes = [
  'en-US',
  'de-DE',
  'fr-FR',
  'es-Es',
  'pt-BR',
  'it-IT',
  'fil',
  'ru-RU',
  'tr-TR',
  'id-ID',
  'pl-PL',
  'ja',
  'ar',
  'nl',
  'fi',
  'da',
  'nb',
  'sv',
] as const;

export type LanguageLocaleCode = (typeof languageLocaleCodes)[number];

export const languageLocaleToCodeMap: Record<LanguageCode, LanguageLocaleCode> = {
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-Es',
  pt: 'pt-BR',
  it: 'it-IT',
  fil: 'fil',
  ru: 'ru-RU',
  tr: 'tr-TR',
  id: 'id-ID',
  pl: 'pl-PL',
  ja: 'ja',
  ar: 'ar',
  nl: 'nl',
  fi: 'fi',
  da: 'da',
  nb: 'nb',
  sv: 'sv',
};

export const supportedlanguageCodesInOnboarding = [
  'de',
  'en',
  'es',
  'fr',
  'it',
  'nl',
  'pl',
  'pt-br',
  'sv',
  'tr',
] as const;

export type SupportedLanguageCodeInOnboarding = (typeof supportedlanguageCodesInOnboarding)[number];

/**
 * Checks if the given value is a valid language code for onboarding users.
 * @param value {string}
 * @returns {boolean}
 */
export const isLanguageCodeOnboardingSupported = (value: string): value is SupportedLanguageCodeInOnboarding =>
  supportedlanguageCodesInOnboarding.includes(value as SupportedLanguageCodeInOnboarding);
